import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

const STORAGE = {
  access: "po.access",
  refresh: "po.refresh",
  tenant: "po.tenant",
};

export const tokens = {
  get access() {
    return localStorage.getItem(STORAGE.access);
  },
  get refresh() {
    return localStorage.getItem(STORAGE.refresh);
  },
  set(access: string, refresh?: string) {
    localStorage.setItem(STORAGE.access, access);
    if (refresh) localStorage.setItem(STORAGE.refresh, refresh);
  },
  clear() {
    localStorage.removeItem(STORAGE.access);
    localStorage.removeItem(STORAGE.refresh);
  },
};

export const activeTenant = {
  get(): string | null {
    return localStorage.getItem(STORAGE.tenant);
  },
  set(slug: string) {
    localStorage.setItem(STORAGE.tenant, slug);
  },
};

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const access = tokens.access;
  if (access) config.headers.set("Authorization", `Bearer ${access}`);
  const tenant = activeTenant.get();
  if (tenant) config.headers.set("X-Tenant", tenant);
  return config;
});

let refreshing: Promise<string | null> | null = null;

async function refreshAccess(): Promise<string | null> {
  const refresh = tokens.refresh;
  if (!refresh) return null;
  try {
    const { data } = await axios.post(`${API_BASE}/auth/refresh/`, { refresh });
    tokens.set(data.access);
    return data.access as string;
  } catch {
    tokens.clear();
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      refreshing = refreshing ?? refreshAccess();
      const newAccess = await refreshing;
      refreshing = null;
      if (newAccess) {
        original.headers.set("Authorization", `Bearer ${newAccess}`);
        return api(original);
      }
    }
    return Promise.reject(error);
  }
);

export { API_BASE };
